import { PushNotifications } from "@capacitor/push-notifications";
import { userApi } from "@/lib/api";
import { isNative, platform } from "./capacitor";

/** Android channel for "new activity" pushes; the server addresses it by id. */
export const ANDROID_CHANNEL_ID = "activities";

/** The token this launch registered, so logout can unregister it. */
let lastToken: string | null = null;
export const currentNativePushToken = (): string | null => lastToken;

export type PushHandlers = {
  /** A notification was tapped: navigate to the run it is about. */
  onTap: (path: string) => void;
  /** A notification arrived while the app was open: refresh what it is about. */
  onReceived?: () => void;
};

/**
 * Registers for push on native builds and posts the device token to the API.
 * Returns an unsubscribe. No-op on the web (browser push lives in lib/web).
 */
export function setupPushNotifications(handlers: PushHandlers | ((path: string) => void)): () => void {
  if (!isNative()) return () => {};
  const { onTap, onReceived } = typeof handlers === "function" ? { onTap: handlers } : handlers;
  const subs: Promise<{ remove: () => Promise<void> }>[] = [];

  void (async () => {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt") perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return;
    if (platform() === "android") {
      // Android 8+ needs a channel before anything can be shown; the server
      // sends to this id, and a missing channel means a silent drop.
      await PushNotifications.createChannel({
        id: ANDROID_CHANNEL_ID,
        name: "New activities",
        description: "A run has been uploaded",
        importance: 4,
        visibility: 1,
      }).catch(() => {});
    }
    await PushNotifications.register();
  })();

  subs.push(
    PushNotifications.addListener("registration", (token) => {
      lastToken = token.value;
      void userApi
        .registerDevice(token.value, platform() === "ios" ? "IOS" : "ANDROID")
        .catch((err) => console.warn("[push] token registration failed", err));
    }),
  );
  subs.push(
    PushNotifications.addListener("registrationError", (err) => {
      // Simulators and offline launches land here; nothing to show the user.
      console.warn("[push] registration failed", err);
    }),
  );
  subs.push(
    PushNotifications.addListener("pushNotificationReceived", () => {
      onReceived?.();
    }),
  );
  subs.push(
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = (action.notification.data ?? {}) as { activityId?: string };
      onTap(data.activityId ? `/runs/${data.activityId}` : "/home");
    }),
  );
  return () => {
    for (const s of subs) void s.then((h) => h.remove());
  };
}
